import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  AlignCenterHorizontal,
  AlignCenterVertical,
  Loader2,
  RotateCcw,
  Undo2,
  X,
} from "lucide-react";
import { api, BACKEND_BASE } from "@/lib/api";
import {
  centerRect,
  cleanFgRect,
  clampCanvas,
  coverRect,
  defaultFgRect,
  isCentered,
  maxZoomPercent,
  minZoomPercent,
  moveRect,
  openingState,
  previewStyles,
  rectForZoom,
  resizeRect,
  stageBox,
  zoomPercent,
} from "@/lib/horizontalize";
import { tr } from "@/lib/i18n";
import { trRich } from "@/lib/i18nRich";

const BTN =
  "inline-flex items-center gap-1.5 px-3 py-1.5 text-sm border border-[var(--border)] hover:bg-[var(--secondary)] disabled:opacity-40 disabled:cursor-not-allowed";

// Corner handles: which corner stays fixed (as fractions of the photo)
// and which way a rightward drag changes the width.
const HANDLES = [
  { id: "tl", ax: 1, ay: 1, dir: -1, pos: { left: -7, top: -7 }, cursor: "nwse-resize" },
  { id: "tr", ax: 0, ay: 1, dir: 1, pos: { right: -7, top: -7 }, cursor: "nesw-resize" },
  { id: "bl", ax: 1, ay: 0, dir: -1, pos: { left: -7, bottom: -7 }, cursor: "nesw-resize" },
  { id: "br", ax: 0, ay: 0, dir: 1, pos: { right: -7, bottom: -7 }, cursor: "nwse-resize" },
];

// The slider stops here even though the limits allow more: past 6x
// "fit" is reachable with the handles but useless on a slider.
const SLIDER_MAX = 600;

/**
 * Horizontalize — turn the article's image into a landscape picture.
 *
 * A landscape canvas (1300 x 800 by default), the photo stretched
 * across the full width and blurred behind, and the photo itself on
 * top with a soft dark glow. Drag the photo to move it; drag a corner
 * or use the slider to resize it.
 *
 * What is on screen is a CSS preview (a blurred <img> and a
 * box-shadow); Save sends the placement to the server, which draws the
 * real image with the same geometry (lib/horizontalize.js mirrors
 * backend/image_horizontalize.py).
 *
 * Reopened on an image that is already horizontalized, it starts from
 * the ORIGINAL photo with the last placement, and offers to put the
 * original back.
 */
export default function HorizontalizeDialog({ article, onClose, onSaved }) {
  // Decided once: the article refetching mid-edit must not reset the tool.
  const opening = useMemo(() => openingState(article), []); // eslint-disable-line react-hooks/exhaustive-deps
  const src = opening.sourceUrl ? `${BACKEND_BASE}${opening.sourceUrl}` : "";

  const [nat, setNat] = useState(null); // { w, h } of the photo
  const [loadError, setLoadError] = useState(false);
  const [canvas, setCanvas] = useState(opening.canvas);
  const [wIn, setWIn] = useState(String(opening.canvas.w));
  const [hIn, setHIn] = useState(String(opening.canvas.h));
  const [rect, setRect] = useState(null);
  const [stageW, setStageW] = useState(0);
  const stageRef = useRef(null);
  const dragRef = useRef(null);
  const backdropDownRef = useRef(false);

  // The photo's real size.
  useEffect(() => {
    if (!src) return undefined;
    let alive = true;
    const img = new window.Image();
    img.onload = () => {
      if (!alive) return;
      if (img.naturalWidth > 1 && img.naturalHeight > 1) {
        setNat({ w: img.naturalWidth, h: img.naturalHeight });
      } else {
        setLoadError(true);
      }
    };
    img.onerror = () => alive && setLoadError(true);
    img.src = src;
    return () => {
      alive = false;
    };
  }, [src]);

  // First placement, once the size is known.
  useEffect(() => {
    if (!nat || rect) return;
    setRect(
      opening.rect
        ? cleanFgRect(opening.rect, nat.w, nat.h, canvas.w, canvas.h)
        : defaultFgRect(nat.w, nat.h, canvas.w, canvas.h)
    );
  }, [nat, rect, opening, canvas]);

  // Stage width in screen pixels → the preview scale.
  useEffect(() => {
    const el = stageRef.current;
    if (!el) return undefined;
    const measure = () => setStageW(el.clientWidth);
    measure();
    window.addEventListener("resize", measure);
    let ro = null;
    if (typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(measure);
      ro.observe(el);
    }
    return () => {
      window.removeEventListener("resize", measure);
      if (ro) ro.disconnect();
    };
  }, [nat]);

  const save = useMutation({
    mutationFn: () =>
      api.horizontalizeArticleImage(article.id, {
        canvas_w: canvas.w,
        canvas_h: canvas.h,
        x: rect.x,
        y: rect.y,
        w: rect.w,
      }),
    onSuccess: (updated) => {
      toast.success(tr("Saved as a {w} × {h} image", { w: canvas.w, h: canvas.h }));
      onSaved(updated);
      onClose();
    },
    onError: (e) =>
      toast.error(e?.response?.data?.detail || e?.message || tr("Could not save the image")),
  });
  const revert = useMutation({
    mutationFn: () => api.revertHorizontalizedImage(article.id),
    onSuccess: (updated) => {
      toast.success(tr("Original image restored"));
      onSaved(updated);
      onClose();
    },
    onError: (e) =>
      toast.error(e?.response?.data?.detail || e?.message || tr("Could not restore the original")),
  });
  const busy = save.isPending || revert.isPending;

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape" && !busy) onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [busy, onClose]);

  const scale = stageW > 0 ? stageW / canvas.w : 0;
  const ready = !!(nat && rect && scale > 0);

  const commitCanvas = () => {
    const next = clampCanvas(wIn, hIn);
    setWIn(String(next.w));
    setHIn(String(next.h));
    if (next.w === canvas.w && next.h === canvas.h) return;
    setCanvas(next);
    // A new canvas shape: start again from "whole photo, centred".
    if (nat) setRect(defaultFgRect(nat.w, nat.h, next.w, next.h));
  };

  const startDrag = (e, handle) => {
    if (!ready || busy) return;
    e.preventDefault();
    e.stopPropagation();
    e.currentTarget.setPointerCapture?.(e.pointerId);
    dragRef.current = { x: e.clientX, y: e.clientY, rect, handle };
  };
  const onDrag = (e) => {
    const d = dragRef.current;
    if (!d || !ready) return;
    const dx = (e.clientX - d.x) / scale;
    const dy = (e.clientY - d.y) / scale;
    if (d.handle) {
      setRect(
        resizeRect(d.rect, d.rect.w + dx * d.handle.dir, d.handle.ax, d.handle.ay,
          nat.w, nat.h, canvas.w, canvas.h)
      );
    } else {
      setRect(moveRect(d.rect, dx, dy, nat.w, nat.h, canvas.w, canvas.h));
    }
  };
  const endDrag = () => {
    dragRef.current = null;
  };

  const css = previewStyles(canvas.w, scale);
  const bg = nat ? coverRect(nat.w, nat.h, canvas.w, canvas.h) : null;
  const zoom = ready ? zoomPercent(rect, nat.w, nat.h, canvas.w, canvas.h) : 100;
  const zoomMin = nat ? minZoomPercent(nat.w, nat.h, canvas.w, canvas.h) : 10;
  const zoomMax = nat
    ? Math.min(SLIDER_MAX, maxZoomPercent(nat.w, nat.h, canvas.w, canvas.h))
    : SLIDER_MAX;

  return (
    <div
      role="dialog"
      className="fixed inset-0 z-[60] bg-black/50 flex items-center justify-center p-3 sm:p-4"
      onMouseDown={(e) => {
        backdropDownRef.current = e.target === e.currentTarget;
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget && backdropDownRef.current && !busy) onClose();
      }}
    >
      <div
        data-testid="horizontalize-dialog"
        className="w-full max-w-4xl max-h-full bg-white border border-[var(--border)] shadow-xl flex flex-col overflow-hidden"
      >
        <div className="flex items-center justify-between px-5 py-3 border-b border-[var(--border)]">
          <div>
            <div className="overline">{tr("Article image")}</div>
            <h2 className="font-serif-display text-lg">{tr("Horizontalize · {w} × {h}", { w: canvas.w, h: canvas.h })}
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            data-testid="horizontalize-close"
            className="text-[var(--ink-muted)] hover:text-[var(--ink)] disabled:opacity-40"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-4 sm:p-5 space-y-4 overflow-auto">
          <p className="text-xs text-[var(--ink-muted)] leading-relaxed">{tr("The photo fills the canvas as a blurred background, and sits on top with a soft dark glow. Drag the photo to move it. Drag a corner, or use the slider, to make it larger and focus on part of it. The Center buttons put it back in the middle without changing its size.")}</p>

          {loadError ? (
            <div className="text-xs px-3 py-2 border bg-red-50 text-red-700 border-red-200">{tr("The image could not be loaded. Close this and upload it again.")}</div>
          ) : (
            <div
              ref={stageRef}
              data-testid="horizontalize-stage"
              className="relative w-full overflow-hidden bg-black select-none"
              style={{ aspectRatio: `${canvas.w} / ${canvas.h}`, touchAction: "none" }}
            >
              {!ready && (
                <div className="absolute inset-0 flex items-center justify-center">
                  <Loader2 className="w-5 h-5 animate-spin text-white" />
                </div>
              )}
              {ready && (
                <>
                  <img
                    src={src}
                    alt=""
                    draggable={false}
                    className="absolute max-w-none pointer-events-none"
                    style={{ ...stageBox(bg, scale), filter: `blur(${css.blurPx}px)` }}
                  />
                  <div
                    data-testid="horizontalize-photo"
                    onPointerDown={(e) => startDrag(e, null)}
                    onPointerMove={onDrag}
                    onPointerUp={endDrag}
                    onPointerCancel={endDrag}
                    className="absolute cursor-move"
                    style={{
                      ...stageBox(rect, scale),
                      boxShadow: `0 0 ${css.glowPx}px ${css.glowColor}`,
                      touchAction: "none",
                    }}
                  >
                    <img
                      src={src}
                      alt=""
                      draggable={false}
                      className="block w-full h-full max-w-none pointer-events-none"
                    />
                    {HANDLES.map((h) => (
                      <span
                        key={h.id}
                        data-testid={`horizontalize-handle-${h.id}`}
                        onPointerDown={(e) => startDrag(e, h)}
                        onPointerMove={onDrag}
                        onPointerUp={endDrag}
                        onPointerCancel={endDrag}
                        className="absolute w-3.5 h-3.5 bg-white border border-[var(--slate-900)]"
                        style={{ ...h.pos, cursor: h.cursor, touchAction: "none" }}
                      />
                    ))}
                  </div>
                </>
              )}
            </div>
          )}

          <div className="flex flex-wrap items-end gap-x-6 gap-y-3">
            <label className="block text-xs flex-1 min-w-[14rem]">
              {trRich("<0>Photo size · {zoom}%</0>", { zoom }, [<span className="block mb-1 text-[var(--ink-muted)]" />])}
              <input
                type="range"
                data-testid="horizontalize-zoom"
                min={zoomMin}
                max={zoomMax}
                step={1}
                value={Math.min(zoomMax, Math.max(zoomMin, zoom))}
                disabled={!ready || busy}
                onChange={(e) =>
                  setRect(rectForZoom(rect, e.target.value, nat.w, nat.h, canvas.w, canvas.h))
                }
                className="w-full accent-[var(--accent)]"
              />
            </label>
            <button
              type="button"
              data-testid="horizontalize-reset"
              disabled={!ready || busy}
              onClick={() => setRect(defaultFgRect(nat.w, nat.h, canvas.w, canvas.h))}
              className={BTN}
            >
              <RotateCcw className="w-3.5 h-3.5" />{" "}{tr("Fit whole photo")}</button>
            <button
              type="button"
              data-testid="horizontalize-center-x"
              disabled={!ready || busy || isCentered(rect, "x", canvas.w, canvas.h)}
              onClick={() => setRect(centerRect(rect, "x", nat.w, nat.h, canvas.w, canvas.h))}
              title={tr("Centre the photo left to right, keeping its size")}
              className={BTN}
            >
              <AlignCenterVertical className="w-3.5 h-3.5" />{" "}{tr("Center horizontally")}</button>
            <button
              type="button"
              data-testid="horizontalize-center-y"
              disabled={!ready || busy || isCentered(rect, "y", canvas.w, canvas.h)}
              onClick={() => setRect(centerRect(rect, "y", nat.w, nat.h, canvas.w, canvas.h))}
              title={tr("Centre the photo top to bottom, keeping its size")}
              className={BTN}
            >
              <AlignCenterHorizontal className="w-3.5 h-3.5" />{" "}{tr("Center vertically")}</button>
            <div className="flex items-end gap-2 text-xs">
              <label className="block">
                {trRich("<0>Width</0>", null, [<span className="block mb-1 text-[var(--ink-muted)]" />])}
                <input
                  data-testid="horizontalize-width"
                  inputMode="numeric"
                  value={wIn}
                  onChange={(e) => setWIn(e.target.value)}
                  onBlur={commitCanvas}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") e.currentTarget.blur();
                  }}
                  disabled={busy}
                  className="border border-[var(--border)] px-2 py-1.5 text-sm bg-white w-20 outline-none focus:border-[var(--slate-900)]"
                />
              </label>
              <span className="pb-2 text-[var(--ink-muted)]">×</span>
              <label className="block">
                {trRich("<0>Height</0>", null, [<span className="block mb-1 text-[var(--ink-muted)]" />])}
                <input
                  data-testid="horizontalize-height"
                  inputMode="numeric"
                  value={hIn}
                  onChange={(e) => setHIn(e.target.value)}
                  onBlur={commitCanvas}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") e.currentTarget.blur();
                  }}
                  disabled={busy}
                  className="border border-[var(--border)] px-2 py-1.5 text-sm bg-white w-20 outline-none focus:border-[var(--slate-900)]"
                />
              </label>
            </div>
          </div>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-3 border-t border-[var(--border)]">
          {opening.active ? (
            <button
              type="button"
              data-testid="horizontalize-revert"
              onClick={() => revert.mutate()}
              disabled={busy}
              title={tr("Put back the image as it was before it was horizontalized")}
              className={BTN}
            >
              {revert.isPending ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <Undo2 className="w-3.5 h-3.5" />
              )}{tr("Restore original")}</button>
          ) : (
            <span />
          )}
          <div className="flex items-center gap-2">
            <button type="button" onClick={onClose} disabled={busy} className={BTN}>{tr("Cancel")}</button>
            <button
              type="button"
              data-testid="horizontalize-save"
              onClick={() => save.mutate()}
              disabled={!ready || busy}
              className="inline-flex items-center gap-1.5 px-4 py-1.5 text-sm bg-[var(--accent)] text-white hover:bg-[var(--accent-hover)] disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {save.isPending && <Loader2 className="w-3.5 h-3.5 animate-spin" />}{tr("Save image")}</button>
          </div>
        </div>
      </div>
    </div>
  );
}
